import {createApi} from '@reduxjs/toolkit/query/react'
import {baseQueryWithReAuth} from "@/store/features/baseRtkQuery";
import {UserInfo} from "@/services/models/user";

export const usersApiSlice = createApi({
    reducerPath: 'usersApi',
    baseQuery: baseQueryWithReAuth,
    tagTypes: ['DefaultAgent', 'Favorites'] as const,
    endpoints: (builder) => ({
        getUsers: builder.query<UserInfo[], void>({
            query: () => ({
                url: `Users`,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
            }),
            transformResponse: (response: UserInfo[]) =>
                response.map(u => ({
                    ...u,
                    projects: u.projects || []
                }))
        }),
        getEnterpriseRoles: builder.query<string[], void>({
            query: () => ({
                url: 'Users/roles',
                method: 'GET',
            }),
        }),
        getDefaultAgent: builder.query<{ defaultAgentId?: string }, void>({
            query: () => ({
                url: `Users/me/default-agent`,
                method: 'GET',
            }),
            providesTags: ['DefaultAgent'],
        }),
        setDefaultAgent: builder.mutation<void, { agentId?: string | null }>({
            query: ({agentId}) => ({
                url: `Users/me/default-agent`,
                method: 'PUT',
                body: {agentId},
            }),
            // Optimistic cache update to avoid refetch/reorder flicker
            async onQueryStarted({agentId}, {dispatch, queryFulfilled}) {
                const patch = dispatch(
                    usersApiSlice.util.updateQueryData('getDefaultAgent', undefined, (draft) => {
                        draft.defaultAgentId = agentId ?? undefined;
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),
        getFavorites: builder.query<{ favorites: string[] }, void>({
            query: () => ({
                url: `Users/me/favorites`,
                method: 'GET',
            }),
            providesTags: ['Favorites'],
        }),
        toggleFavorite: builder.mutation<{ favorites: string[] }, { agentId: string }>({
            query: ({agentId}) => ({
                url: `Users/me/favorites/toggle`,
                method: 'POST',
                body: {agentId},
            }),
            // Optimistic cache update for favorites
            async onQueryStarted({agentId}, {dispatch, queryFulfilled}) {
                const patch = dispatch(
                    usersApiSlice.util.updateQueryData('getFavorites', undefined, (draft) => {
                        const set = new Set(draft.favorites ?? []);
                        if (set.has(agentId)) set.delete(agentId);
                        else set.add(agentId);
                        draft.favorites = Array.from(set);
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),
    }),
})

const {
    useGetUsersQuery,
    useGetEnterpriseRolesQuery,
    useGetDefaultAgentQuery,
    useSetDefaultAgentMutation,
    useGetFavoritesQuery,
    useToggleFavoriteMutation,
} = usersApiSlice
export const usersApiActions = {
    useGetUsersQuery,
    useGetEnterpriseRolesQuery,
    useGetDefaultAgentQuery,
    useSetDefaultAgentMutation,
    useGetFavoritesQuery,
    useToggleFavoriteMutation,
}